/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, type ChangeEvent, type ReactElement, type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SourceViewPositionState } from '../source-view-utils';

const footerStyles = cssMap({
	footer: {
		alignItems: 'center',
		borderBlockStartColor: token('color.border'),
		borderBlockStartStyle: 'solid',
		borderBlockStartWidth: token('border.width'),
		boxSizing: 'border-box',
		display: 'flex',
		font: token('font.body.small'),
		gap: token('space.200'),
		justifyContent: 'space-between',
		paddingBlockEnd: token('space.100'),
		paddingBlockStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		paddingInlineStart: token('space.100'),
		width: '100%',
	},
	labelText: {
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	leftSection: {
		alignItems: 'center',
		color: token('color.text.subtle'),
		display: 'flex',
		flex: '1 1 auto',
		gap: token('space.200'),
		minWidth: 0,
		overflow: 'hidden',
	},
	lineNumbersControl: {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	rightSection: {
		alignItems: 'center',
		color: token('color.text.subtle'),
		display: 'flex',
		flex: '0 0 auto',
		gap: token('space.100'),
		whiteSpace: 'nowrap',
	},
	countSeparator: {
		color: token('color.text.subtlest'),
	},
	wrapCodeControl: {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	statusText: {
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
});

const fullWidthFooterStyles = css({
	backgroundColor: token('elevation.surface'),
	flexShrink: 0,
	maxWidth: '100%',
	minWidth: 0,
	width: '100%',
});

/**
 * MVP layout: the source view grows to content height and the parent
 * `editor-content-container` owns vertical scrolling, so the status-bar footer
 * would otherwise scroll out of view. Pin it to the bottom of the scroll
 * viewport. The opaque `elevation.surface` background (set above) keeps editor
 * content from showing through as it scrolls behind it.
 */
const stickyFooterStyles = css({
	position: 'sticky',
	bottom: 0,
	zIndex: 1,
});

const DEFAULT_SOURCE_POSITION_STATE = {
	columnNumber: 1,
	lineCount: 1,
	lineNumber: 1,
	wordCount: 0,
} satisfies SourceViewPositionState;

export type MarkdownSourceViewFooterProps = {
	children?: ReactNode;
	onShowLineNumbersChange?: (showLineNumbers: boolean) => void;
	onWrapCodeChange?: (wrapCode: boolean) => void;
	showLineNumbers: boolean;
	sourcePositionState?: SourceViewPositionState | null;
	wrapCode?: boolean;
};

export const MarkdownSourceViewFooter = ({
	children,
	onShowLineNumbersChange,
	onWrapCodeChange,
	showLineNumbers,
	sourcePositionState,
	wrapCode = true,
}: MarkdownSourceViewFooterProps): ReactElement => {
	const {
		columnNumber,
		lineCount,
		lineNumber,
		// `wordCount` is optional on the type for non-breaking adoption; default
		// it so a consumer-supplied state without a word count still renders.
		wordCount = 0,
	} = sourcePositionState ?? DEFAULT_SOURCE_POSITION_STATE;
	const cursorPositionValues = useMemo(
		() => ({ columnNumber, lineNumber }),
		[columnNumber, lineNumber],
	);
	const lineCountValues = useMemo(() => ({ lineCount }), [lineCount]);
	const wordCountValues = useMemo(() => ({ wordCount }), [wordCount]);

	const handleLineNumbersChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			onShowLineNumbersChange?.(event.currentTarget.checked);
		},
		[onShowLineNumbersChange],
	);

	const handleWrapCodeChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			onWrapCodeChange?.(event.currentTarget.checked);
		},
		[onWrapCodeChange],
	);

	return (
		<div css={[fullWidthFooterStyles, fg('platform_editor_md_mvp_layout') && stickyFooterStyles]}>
			<Box xcss={footerStyles.footer} testId="markdown-source-view-footer">
				<Box xcss={footerStyles.leftSection}>
					<Box xcss={footerStyles.lineNumbersControl}>
						<Checkbox
							isChecked={showLineNumbers}
							onChange={handleLineNumbersChange}
							label={
								<FormattedMessage
									id="content-markdown-mode.footer.line-numbers"
									defaultMessage="Line numbers"
									description="Checkbox label for toggling markdown source editor line numbers."
								/>
							}
						/>
					</Box>
					<Box xcss={footerStyles.wrapCodeControl}>
						<Checkbox
							isChecked={wrapCode}
							onChange={handleWrapCodeChange}
							label={
								<FormattedMessage
									id="content-markdown-mode.footer.wrap-code"
									defaultMessage="Wrap code"
									description="Checkbox label for toggling soft line wrapping in the markdown source editor."
								/>
							}
						/>
					</Box>
					{children}
					<Box xcss={footerStyles.statusText}>
						<FormattedMessage
							id="content-markdown-mode.footer.cursor-position"
							defaultMessage="Ln {lineNumber}, Col {columnNumber}"
							description="Current cursor line and column in the markdown source editor footer."
							values={cursorPositionValues}
						/>
					</Box>
					<Box xcss={footerStyles.labelText}>
						<FormattedMessage
							id="content-markdown-mode.footer.cursor-position-label"
							defaultMessage="Position of cursor"
							description="Label shown next to the current cursor line and column in the markdown source editor footer."
						/>
					</Box>
				</Box>
				<Box xcss={footerStyles.rightSection}>
					<Box as="span">
						<FormattedMessage
							id="content-markdown-mode.footer.line-count"
							defaultMessage="{lineCount, plural, one {# line} other {# lines}}"
							description="Total number of lines in the markdown source editor footer."
							values={lineCountValues}
						/>
					</Box>
					<Box as="span" xcss={footerStyles.countSeparator} aria-hidden>
						|
					</Box>
					<Box as="span">
						<FormattedMessage
							id="content-markdown-mode.footer.word-count"
							defaultMessage="{wordCount, plural, one {# word} other {# words}}"
							description="Total number of words in the markdown source editor footer."
							values={wordCountValues}
						/>
					</Box>
				</Box>
			</Box>
		</div>
	);
};
